import type { CollectionSummaryJSONObject, TagJSONObject } from '@ikegps/core-api';
import { action, computed, observable, makeObservable } from 'mobx';
import { sort as timSort } from 'timsort';
import type { MaterialIcon } from '../icon/icons';
import type { T } from '../../i18n';

export type SortablePrimitive = Date | number | string | null;

// Dynamic sort-key lookup: the caller guarantees the key names a sortable field on the JSON object,
// a relationship the typed Parse objects can't express for an arbitrary string key.
function pickSortable(obj: object, key: string): SortablePrimitive {
	return (obj as { [key: string]: SortablePrimitive })[key];
}

/** Maps each sortable domain key to the JSON object type it sorts. */
interface SortableObjects {
	tag: TagJSONObject;
	collection: CollectionSummaryJSONObject;
}

export interface SortableStateOption {
	title: T;
	icon: MaterialIcon;
	/** Stable hook for E2E selectors (rendered as the menu item's data-testid), so specs never
	 * match on the translated label text. */
	testId?: string;
	visible?: () => boolean;
	keys: { [K in keyof SortableObjects]: string | ((obj: SortableObjects[K]) => SortablePrimitive) };
	value?: {
		collection?: (collectionSummary: CollectionSummaryJSONObject) => SortablePrimitive;
	};
	info: {
		tag?: boolean;
		collection?: boolean;
	};
}

export enum SortDirection {
	ASCENDING = 1,
	DESCENDING = -1,
}

export class SortableState {
	@observable direction: SortDirection = SortDirection.DESCENDING;
	@observable option: SortableStateOption | null = null;

	options: SortableStateOption[];

	constructor(sortOptions: SortableStateOption[]) {
		makeObservable(this);

		this.options = sortOptions;
		if (this.options.length > 0) {
			this.option = this.options[0];
		}
	}

	@computed get isAscending() {
		return this.direction === SortDirection.ASCENDING;
	}

	@action.bound toggleDirection() {
		if (this.direction === SortDirection.ASCENDING) {
			this.direction = SortDirection.DESCENDING;
		} else {
			this.direction = SortDirection.ASCENDING;
		}
	}

	@action setSort(sortOption: SortableStateOption) {
		this.option = sortOption;
	}

	sortTags(objs: TagJSONObject[]): TagJSONObject[] {
		return this.sortObjects(objs, 'tag');
	}

	sortSummaries(objs: CollectionSummaryJSONObject[]): CollectionSummaryJSONObject[] {
		return this.sortObjects(objs, 'collection');
	}

	getCollectionDisplayValue(obj: CollectionSummaryJSONObject): SortablePrimitive {
		const currentOption = this.option;
		if (currentOption.info.collection == null || currentOption.info.collection === false) {
			return null;
		}

		if (currentOption.value != null && currentOption.value.collection != null) {
			return currentOption.value.collection(obj);
		}

		const sortKey = currentOption.keys.collection;
		if (typeof sortKey === 'string') {
			return pickSortable(obj, sortKey);
		}

		return sortKey(obj);
	}

	sortObjects<K extends keyof SortableObjects>(objs: SortableObjects[K][], key: K): SortableObjects[K][] {
		const currentOption = this.option;
		const direction = this.direction;
		const sortKey = currentOption.keys[key];
		const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

		function compareFunc(a: SortableObjects[K], b: SortableObjects[K]) {
			let aKey;
			let bKey;
			if (typeof sortKey === 'string') {
				if (sortKey === 'title') {
					aKey = pickSortable(a, 'title') || pickSortable(a, 'subtitle');
					bKey = pickSortable(b, 'title') || pickSortable(b, 'subtitle');
				} else {
					aKey = pickSortable(a, sortKey);
					bKey = pickSortable(b, sortKey);
				}
			} else {
				aKey = sortKey(a);
				bKey = sortKey(b);
			}

			// Ensure a stable sort by using updatedAt and objectId as a extra indexes
			if (aKey === bKey) {
				if (a.updatedAt !== b.updatedAt) {
					aKey = a.updatedAt;
					bKey = b.updatedAt;
				} else {
					aKey = a.objectId;
					bKey = b.objectId;
				}
			}

			// only use collator for title otherwise fallback to default sorting
			if (sortKey === 'title') {
				return collator.compare(aKey as string, bKey as string) * direction;
			}

			// IKE4-2322 Explicitly compare against null and undefined, where equality is problematic.
			if (aKey == null || bKey == null) {
				if (aKey != null && bKey == null) {
					return direction;
				} else if (aKey == null && bKey != null) {
					return -1 * direction;
				}
				return 0;
			} else if (aKey > bKey) {
				return direction;
			} else if (aKey < bKey) {
				return -1 * direction;
			}

			return 0;
		}

		timSort(objs, compareFunc);

		return objs;
	}
}
